import React, { useState, useEffect, useCallback, useRef } from "react";
import "react-circular-progressbar/dist/styles.css";
import {
  User,
  Video,
  MessageSquare,
  ChevronDown,
  ExternalLink,
  ChevronUp,
} from "lucide-react";
import Layout from "../../../components/layout/layout";
import AmountSpend from "../../../assets/dashboard/amountSpend.svg";
import Interaction from "../../../assets/dashboard/interaction.svg";
import QuickAccess from "../../../assets/dashboard/quickAccess.svg";
import QuickExpert from "../../../assets/dashboard/quickExpert.svg";
import ExperttieSmall from "../../../assets/dashboard/ExperttieSmall.png";
import tooltipSmall from "../../../assets/dashboard/tooltipSmall.png";
import videoIconSmall from "../../../assets/dashboard/videoIconSmall.png";
import QuickExpertBlue from "../../../assets/dashboard/quickExpertBlue.svg";
import QuickAnswer from "../../../assets/dashboard/quickAnswer.svg";
import TooltioIcon from "../../../assets/dashboard/tooltip.svg";
import SummeryArrow from "../../../assets/dashboard/summeryArrow.svg";
import SummeryArrowPrimary from "../../../assets/dashboard/summeryArrowPrimary.svg";
import CustomerSummery from "../../../assets/dashboard/customerSummery.svg";
import InteractionsCard from "../../../components/Dashboard/InteractionsCard";
import TooltipBigicon from "../../../assets/dashboard/tooltipbig.svg";

import ChartCard, {
  CircularChart,
  Rating,
  StatsCounter,
} from "../../../components/Dashboard/ChartCard";
import BarChart from "../../../components/Dashboard/BarChart";
import OverallCard from "../../../components/Dashboard/OverallCard";

import DateRangePicker from "../../../components/DateRange/DateRangePicker";

// Add Redux imports
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../store";
import {
  fetchAllSessionActivities,
  fetchAllUsersQuickAccessCounts,
  fetchAllChatSessions,
} from "../../../store/profileSlice";
import { fetchAllTooltipPurchases } from "../../../store/slices/tooltipSlice";

import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../../config/firebase";
import { useAppSelector } from "../../../store/hooks";

const Dashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [activeTab, setActiveTab] = useState<string>("Overall");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const profileData = useAppSelector((state: RootState) => state.profile.user);
  const [dateRange, setDateRange] = useState<{
    start: string | null;
    end: string | null;
  }>({
    start: null,
    end: null,
  });
  const [timeframeDropdownOpen, setTimeframeDropdownOpen] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState("");
  // refs for outside click handling
  const timeframeRef = useRef<HTMLDivElement | null>(null);
  const datePickerRef = useRef<HTMLDivElement | null>(null);

  // Get session activities from Redux store
  const sessionActivities = useSelector(
    (state: RootState) => state.profile.sessionActivities || []
  );
  const fetchLoading = useSelector(
    (state: RootState) => state.profile.fetchLoading
  );
  // Get quick access counts from Redux store
  const quickAccessCounts = useSelector(
    (state: RootState) => state.profile.quickAccessCounts
  );

  // Get chat sessions from Redux store
  const allChatSessions = useSelector(
    (state: RootState) => state.profile.allChatSessions || []
  );
  const chatSessionsLoading = useSelector(
    (state: RootState) => state.profile.chatSessionsLoading
  );
  // Get tooltip purchases total from Redux store
  const allTooltipPurchases = useSelector(
    (state: RootState) => state.tooltip.allTooltipPurchases
  );
  // Add stats dropdown state variables
  const [statsDropdownOpen, setStatsDropdownOpen] = useState(false);
  const [selectedStatsOption, setSelectedStatsOption] = useState("");
  const [expertsCount, setExpertsCount] = useState(0);
  const [customersCount, setCustomersCount] = useState(0);
  const [activeExpertsCount, setActiveExpertsCount] = useState(0);
  const [activatedExpertsCount, setActivatedExpertsCount] = useState(0);
  const [activatedCustomersCount, setActivatedCustomersCount] = useState(0);
  const [totalActivitiesCount, setTotalActivitiesCount] = useState(0);
  const [averageRatePerMin, setAverageRatePerMin] = useState(0);
  const [expertRatingCounts, setExpertRatingCounts] = useState({
    5: 0,
    4: 0,
    3: 0,
    2: 0,
    1: 0,
  });
  const [customerRatingCounts, setCustomerRatingCounts] = useState({
    5: 0,
    4: 0,
    3: 0,
    2: 0,
    1: 0,
  });
  const [filteredTooltipTotal, setFilteredTooltipTotal] = useState(0);
  const [filteredTooltipCount, setFilteredTooltipCount] = useState(0);
  const [filteredTooltipSatisfaction, setFilteredTooltipSatisfaction] = useState(0);
  const [filteredTooltipRatingCounts, setFilteredTooltipRatingCounts] = useState({
    5: 0,
    4: 0,
    3: 0,
    2: 0,
    1: 0,
  });

  // Fetch session activities when component mounts
  useEffect(() => {
    dispatch(fetchAllSessionActivities());
    dispatch(fetchAllUsersQuickAccessCounts());
    dispatch(fetchAllChatSessions());
    dispatch(fetchAllTooltipPurchases());
  }, [dispatch]); // State for all users and session activities data
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [allSessionActivities, setAllSessionActivities] = useState<any[]>([]);
  console.log(allSessionActivities, "allSessionActivities");
  // Fetch all users and session activities once
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // Fetch all users
        const usersRef = collection(db, "users");
        const usersSnapshot = await getDocs(usersRef);
        const usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAllUsers(usersData); // Filter out users without a role

        // Fetch all session activities
        const sessionActivityRef = collection(db, "sessionActivity");
        const sessionSnapshot = await getDocs(sessionActivityRef);
        const sessionData = sessionSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAllSessionActivities(sessionData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchAllData();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // If timeframe dropdown is open and click is outside its container, close it
      if (
        timeframeDropdownOpen &&
        timeframeRef.current &&
        !timeframeRef.current.contains(target)
      ) {
        setTimeframeDropdownOpen(false);
      }

      // If date picker is open and click is outside its container, close it
      if (
        showDatePicker &&
        datePickerRef.current &&
        !datePickerRef.current.contains(target)
      ) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [timeframeDropdownOpen, showDatePicker]);
  // Calculate filtered counts based on timeframe
  useEffect(() => {
    // Filter users based on timeframe
    const filteredUsers = filterUsersByTimeframe(allUsers);

    // Filter experts
    const experts = filteredUsers.filter((user) => user.role === "Expert");
    setExpertsCount(experts.length);
    setActivatedExpertsCount(
      experts.filter((expert) => !expert?.disabled).length
    );

    // Filter customers
    const customers = filteredUsers.filter((user) => user.role === "Customer");
    setCustomersCount(customers.length);

    setActivatedCustomersCount(
      customers.filter((customer) => !customer?.disabled).length
    );
    // Filter session activities based on timeframe
    const filteredActivities =
      filterActivitiesByTimeframe(allSessionActivities);
    setTotalActivitiesCount(filteredActivities.length);

    // Calculate active experts from filtered activities
    const uniqueExpertIds = new Set();
    filteredActivities.forEach((activity) => {
      if (activity.expertId) {
        uniqueExpertIds.add(activity.expertId);
      }
    });
    setActiveExpertsCount(uniqueExpertIds.size);
  }, [
    allUsers,
    allSessionActivities,
    selectedTimeframe,
    dateRange.start,
    dateRange.end,
  ]);

  // Fetch average rate per minute
  useEffect(() => {
    const fetchAverageRatePerMin = async () => {
      try {
        const profilesRef = collection(db, "profiles");
        const q = query(profilesRef, where("type", "==", "Expert"));
        const querySnapshot = await getDocs(q);

        let totalRate = 0;
        let expertsWithRate = 0;

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.ratePerMinute && !isNaN(parseFloat(data.ratePerMinute))) {
            totalRate += parseFloat(data.ratePerMinute);
            expertsWithRate++;
          }
        });

        const avgRate = expertsWithRate > 0 ? totalRate / expertsWithRate : 0;
        setAverageRatePerMin(Math.round(avgRate * 100) / 100); // Round to 2 decimal places
      } catch (error) {
        console.error("Error fetching average rate per minute:", error);
        setAverageRatePerMin(0);
      }
    };
    fetchAverageRatePerMin();
  }, []);
  // Calculate expert and customer ratings from sessionActivities
  useEffect(() => {
    if (sessionActivities.length > 0) {
      const expertRatings = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
      const customerRatings = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

      sessionActivities.forEach((activity) => {
        // Count expert ratings
        if (activity.expertRating) {
          const expertRating = Math.round(parseFloat(activity.customerRatings));
          if (expertRating >= 1 && expertRating <= 5) {
            expertRatings[expertRating as keyof typeof expertRatings]++;
          }
        }

        // Count customer ratings
        if (activity.customerRatings) {
          const customerRating = Math.round(
            parseFloat(activity.customerRatings)
          );
          if (customerRating >= 1 && customerRating <= 5) {
            customerRatings[customerRating as keyof typeof customerRatings]++;
          }
        }
      });

      setExpertRatingCounts(expertRatings);
      setCustomerRatingCounts(customerRatings);
    }
  }, [sessionActivities]); // Helper function to filter activities based on timeframe
  const filterActivitiesByTimeframe = (activities: any[]) => {
    if (!activities || activities.length === 0) return [];

    const now = new Date();

    return activities.filter((activity) => {
      // Get activity date from various timestamp formats
      let activityDate: Date | null = null;

      // Handle Firestore timestamp objects and regular Date objects
      if (activity.createdAt) {
        activityDate = activity.createdAt.toDate
          ? activity.createdAt.toDate()
          : new Date(activity.createdAt);
      } else if (activity.startTime) {
        activityDate = activity.startTime.toDate
          ? activity.startTime.toDate()
          : new Date(activity.startTime);
      } else if (activity.timestamp) {
        activityDate = activity.timestamp.toDate
          ? activity.timestamp.toDate()
          : activity.timestamp.seconds
          ? new Date(activity.timestamp.seconds * 1000)
          : new Date(activity.timestamp);
      } else if (activity.endTime) {
        activityDate = activity.endTime.toDate
          ? activity.endTime.toDate()
          : activity.endTime.seconds
          ? new Date(activity.endTime.seconds * 1000)
          : new Date(activity.endTime);
      } else if (activity.updatedAt) {
        activityDate = activity.updatedAt.toDate
          ? activity.updatedAt.toDate()
          : activity.updatedAt.seconds
          ? new Date(activity.updatedAt.seconds * 1000)
          : new Date(activity.updatedAt);
      } else if (activity.date) {
        activityDate = new Date(activity.date);
      }

      // If no valid date found, include the activity
      if (!activityDate || isNaN(activityDate.getTime())) {
        return true;
      }

      // Apply custom date range filter first
      if (dateRange.start && dateRange.end) {
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999); // Include the entire end date

        return activityDate >= startDate && activityDate <= endDate;
      }

      // Apply predefined timeframe filters
      switch (selectedTimeframe) {
        case "this-month":
          const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          return activityDate >= thisMonthStart;

        case "last-3-months":
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          return activityDate >= threeMonthsAgo;

        case "last-12-months":
          const twelveMonthsAgo = new Date();
          twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
          return activityDate >= twelveMonthsAgo;

        case "ytd":
          const yearStart = new Date(now.getFullYear(), 0, 1);
          return activityDate >= yearStart;

        default:
          return true; // Show all if no timeframe selected
      }
    });
  };

  // Helper function to filter users based on timeframe
  const filterUsersByTimeframe = (users: any[]) => {
    if (!users || users.length === 0) return [];

    const now = new Date();

    return users.filter((user) => {
      // Get user registration date
      let userDate: Date | null = null;

      if (user.createdAt) {
        userDate = user.createdAt.toDate
          ? user.createdAt.toDate()
          : new Date(user.createdAt);
      } else if (user.registrationDate) {
        userDate = user.registrationDate.toDate
          ? user.registrationDate.toDate()
          : new Date(user.registrationDate);
      } else if (user.joinedAt) {
        userDate = user.joinedAt.toDate
          ? user.joinedAt.toDate()
          : new Date(user.joinedAt);
      }

      // If no valid date found, include the user (for "All time")
      if (!userDate || isNaN(userDate.getTime())) {
        return selectedTimeframe === "" && !dateRange.start && !dateRange.end;
      }

      // Apply custom date range filter first
      if (dateRange.start && dateRange.end) {
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999);

        return userDate >= startDate && userDate <= endDate;
      }

      // Apply predefined timeframe filters
      switch (selectedTimeframe) {
        case "this-month":
          const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          return userDate >= thisMonthStart;

        case "last-3-months":
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          return userDate >= threeMonthsAgo;

        case "last-12-months":
          const twelveMonthsAgo = new Date();
          twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
          return userDate >= twelveMonthsAgo;

        case "ytd":
          const yearStart = new Date(now.getFullYear(), 0, 1);
          return userDate >= yearStart;

        default:
          return true; // Show all if no timeframe selected
      }
    });
  };

  // Helper function to filter tooltip purchases based on timeframe
  const filterPurchasesByTimeframe = useCallback((purchases: any[]) => {
    if (!purchases || purchases.length === 0) return [];

    const now = new Date();

    return purchases.filter((purchase) => {
      // Get purchase date
      let purchaseDate: Date | null = null;

      if (purchase.purchasedAt) {
        if (purchase.purchasedAt.toDate) {
          purchaseDate = purchase.purchasedAt.toDate();
        } else if (purchase.purchasedAt.seconds) {
          purchaseDate = new Date(purchase.purchasedAt.seconds * 1000);
        } else {
          purchaseDate = new Date(purchase.purchasedAt);
        }
      }

      // If no valid date found, include the purchase
      if (!purchaseDate || isNaN(purchaseDate.getTime())) {
        return true;
      }

      // Apply custom date range filter first
      if (dateRange.start && dateRange.end) {
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999);

        return purchaseDate >= startDate && purchaseDate <= endDate;
      }

      // Apply predefined timeframe filters
      switch (selectedTimeframe) {
        case "this-month":
          const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          return purchaseDate >= thisMonthStart;

        case "last-3-months":
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          return purchaseDate >= threeMonthsAgo;

        case "last-12-months":
          const twelveMonthsAgo = new Date();
          twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
          return purchaseDate >= twelveMonthsAgo;

        case "ytd":
          const yearStart = new Date(now.getFullYear(), 0, 1);
          return purchaseDate >= yearStart;

        default:
          return true; // Show all if no timeframe selected
      }
    });
  }, [selectedTimeframe, dateRange.start, dateRange.end]); // Filter session activities based on timeframe and date range
  const filteredSessionActivities =
    filterActivitiesByTimeframe(sessionActivities);

  useEffect(() => {
    const expertRatings = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    const customerRatings = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

    filteredSessionActivities.forEach((activity) => {
      // Count expert ratings
      if (activity.expertRating) {
        const expertRating = Math.round(parseFloat(activity.expertRating));
        if (expertRating >= 1 && expertRating <= 5) {
          expertRatings[expertRating as keyof typeof expertRatings]++;
        }
      }

      // Count customer ratings
      if (activity.customerRating) {
        const customerRating = Math.round(parseFloat(activity.customerRating));
        if (customerRating >= 1 && customerRating <= 5) {
          customerRatings[customerRating as keyof typeof customerRatings]++;
        }
      }
    });

    setExpertRatingCounts(expertRatings);
    setCustomerRatingCounts(customerRatings);
  }, [
    filteredSessionActivities,
    selectedTimeframe,
    dateRange.start,
    dateRange.end,
  ]);

  // Calculate filtered tooltip purchases stats
  useEffect(() => {
    const filteredPurchases = filterPurchasesByTimeframe(allTooltipPurchases);
    let total = 0;
    let count = 0;
    let highRating = 0;
    const ratings = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

    filteredPurchases.forEach((purchase) => {
      const price = typeof purchase.price === "number" ? purchase.price : parseFloat(purchase.price) || 0;
      const rating = typeof purchase.rating === "number" ? purchase.rating : parseFloat(purchase.rating) || 0;
      total += price;
      count += 1;
      if (rating >= 1 && rating <= 5) {
        const rounded = Math.round(rating);
        ratings[rounded as keyof typeof ratings]++;
      }
      if (rating >= 4) {
        highRating += 1;
      }
    });

    const satisfaction = count > 0 ? Math.round((highRating / count) * 100) : 0;

    setFilteredTooltipTotal(total);
    setFilteredTooltipCount(count);
    setFilteredTooltipSatisfaction(satisfaction);
    setFilteredTooltipRatingCounts(ratings);
  }, [allTooltipPurchases, filterPurchasesByTimeframe]);

  // Fetch tooltip purchases with timeframe filtering
  // Removed, now using real-time listener

  // Calculate session statistics from filtered sessionActivities
  const sessionStats = {
    totalSessions: filteredSessionActivities.length,
    totalInteractions: filteredSessionActivities.length,
    totalEarnings: filteredSessionActivities.reduce((sum, activity) => {
      const cost = parseFloat(activity.cost || activity.amount || "0");
      return sum + cost;
    }, 0),
    quickExpertSessions: filteredSessionActivities.filter(
      (activity) => activity.type === "Video Call" || activity.type === "Chat"
    ).length,
    videoCalls: filteredSessionActivities.filter(
      (activity) => activity.type === "Video Call"
    ).length,
    chatSessions: filteredSessionActivities.filter(
      (activity) => activity.type === "Chat"
    ).length,
    highRatedSessions: filteredSessionActivities.filter((activity) => {
      const rating = parseFloat(activity.expertRating || "0");
      return rating >= 4;
    }).length,
    satisfactionScore:
      filteredSessionActivities.length > 0
        ? Math.round(
            (filteredSessionActivities.filter((activity) => {
              const rating = parseFloat(activity.expertRating || "0");
              return rating >= 4;
            }).length /
              filteredSessionActivities.length) *
              100
          )
        : 0,
    satisfactionScoreExpert:
      filteredSessionActivities.length > 0
        ? Math.round(
            (filteredSessionActivities.filter((activity) => {
              const rating = parseFloat(activity.customerRating || "0");
              return rating >= 4;
            }).length /
              filteredSessionActivities.length) *
              100
          )
        : 0,
  };
  // Filter chat sessions based on timeframe and date range
  const filteredChatSessions = (() => {
    if (!allChatSessions || allChatSessions.length === 0) return [];
    const now = new Date();
    return allChatSessions.filter((session: any) => {
      let sessionDate: Date | null = null;
      if (session.createdAt) {
        sessionDate = session.createdAt.toDate
          ? session.createdAt.toDate()
          : new Date(session.createdAt);
      }
      if (!sessionDate || isNaN(sessionDate.getTime())) {
        return selectedTimeframe === "" && !dateRange.start && !dateRange.end;
      }
      if (dateRange.start && dateRange.end) {
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999);
        return sessionDate >= startDate && sessionDate <= endDate;
      }
      switch (selectedTimeframe) {
        case "this-month":
          const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          return sessionDate >= thisMonthStart;
        case "last-3-months":
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          return sessionDate >= threeMonthsAgo;
        case "last-12-months":
          const twelveMonthsAgo = new Date();
          twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
          return sessionDate >= twelveMonthsAgo;
        case "ytd":
          const yearStart = new Date(now.getFullYear(), 0, 1);
          return sessionDate >= yearStart;
        default:
          return true;
      }
    });
  })();

  // Calculate Quick Answers statistics
  const totalChatSessionsWithRating = filteredChatSessions.filter((session: any) => session.rating).length;
  const quickAnswersStats = {
    totalSessions: filteredChatSessions.length,
    highRatedSessions: filteredChatSessions.filter((session: any) => {
      const rating = parseFloat(session.rating || "0");
      return rating >= 4;
    }).length,

    satisfactionScore:
      filteredChatSessions.length > 0
        ? Math.round(
            (filteredChatSessions.filter((session: any) => {
              const rating = parseFloat(session.rating || "0");
              return rating >= 4;
            }).length /
              totalChatSessionsWithRating) *
              100
          )
        : 0,
  };
  // Calculate average earning per expert
  const averageEarningPerExpert =
    expertsCount > 0
      ? (sessionStats.totalEarnings / expertsCount).toFixed(2)
      : "0.00";
  const averageSpendsPerCustomer =
    expertsCount > 0
      ? (sessionStats.totalEarnings / customersCount).toFixed(2)
      : "0.00";

  // Calculate average activity per expert
  const averageActivityPerExpert =
    expertsCount > 0
      ? (totalActivitiesCount / expertsCount).toFixed(2)
      : "0.00";
  const averageActivityPerCustomer =
    expertsCount > 0
      ? (totalActivitiesCount / customersCount).toFixed(2)
      : "0.00";

  // Calculate expert rating percentages
  const totalExpertRatings = Object.values(expertRatingCounts).reduce(
    (sum, count) => sum + count,
    0
  );
  const expertSessionRatings: Rating[] = [
    {
      stars: 5,
      percentage:
        totalExpertRatings > 0
          ? Math.round((expertRatingCounts[5] / totalExpertRatings) * 100)
          : 0,
      count: expertRatingCounts[5],
    },
    {
      stars: 4,
      percentage:
        totalExpertRatings > 0
          ? Math.round((expertRatingCounts[4] / totalExpertRatings) * 100)
          : 0,
      count: expertRatingCounts[4],
    },
    {
      stars: 3,
      percentage:
        totalExpertRatings > 0
          ? Math.round((expertRatingCounts[3] / totalExpertRatings) * 100)
          : 0,
      count: expertRatingCounts[3],
    },
    {
      stars: 2,
      percentage:
        totalExpertRatings > 0
          ? Math.round((expertRatingCounts[2] / totalExpertRatings) * 100)
          : 0,
      count: expertRatingCounts[2],
    },
    {
      stars: 1,
      percentage:
        totalExpertRatings > 0
          ? Math.round((expertRatingCounts[1] / totalExpertRatings) * 100)
          : 0,
      count: expertRatingCounts[1],
    },
  ];

  // Calculate customer rating percentages
  const totalCustomerRatings = Object.values(customerRatingCounts).reduce(
    (sum, count) => sum + count,
    0
  );
  const customerSessionRatings: Rating[] = [
    {
      stars: 5,
      percentage:
        totalCustomerRatings > 0
          ? Math.round((customerRatingCounts[5] / totalCustomerRatings) * 100)
          : 0,
      count: customerRatingCounts[5],
    },
    {
      stars: 4,
      percentage:
        totalCustomerRatings > 0
          ? Math.round((customerRatingCounts[4] / totalCustomerRatings) * 100)
          : 0,
      count: customerRatingCounts[4],
    },
    {
      stars: 3,
      percentage:
        totalCustomerRatings > 0
          ? Math.round((customerRatingCounts[3] / totalCustomerRatings) * 100)
          : 0,
      count: customerRatingCounts[3],
    },
    {
      stars: 2,
      percentage:
        totalCustomerRatings > 0
          ? Math.round((customerRatingCounts[2] / totalCustomerRatings) * 100)
          : 0,
      count: customerRatingCounts[2],
    },
    {
      stars: 1,
      percentage:
        totalCustomerRatings > 0
          ? Math.round((customerRatingCounts[1] / totalCustomerRatings) * 100)
          : 0,
      count: customerRatingCounts[1],
    },
  ];
  const timeframeOptions = [
    { value: "", label: "Select timeframe" },
    { value: "this-month", label: "This month" },
    { value: "last-3-months", label: "Last 3 months" },
    { value: "last-12-months", label: "Last 12 months" },
    { value: "ytd", label: "YTD" },
  ];

  // Add stats options
  const statsOptions = [
    { value: "Me", label: "Me" },
    { value: "All", label: "All" },
    { value: "User1", label: "User 1 (Role)" },
    { value: "User2", label: "User 2 (Role)" },
    { value: "User3", label: "User 3 (Role)" },
  ];
  const handleTimeframeSelect = (value: string) => {
    setSelectedTimeframe(value);
    setTimeframeDropdownOpen(false);
    // Reset custom date range when selecting a predefined timeframe
    if (value !== "custom") {
      setDateRange({ start: null, end: null });
    }
  }; // Add stats selection handler
  const handleStatsSelect = (_value: string, label: string) => {
    setSelectedStatsOption(label);
    setStatsDropdownOpen(false);
  };
  const handleDateRangeApply = (
    startDate: Date | null,
    endDate: Date | null
  ) => {
    setDateRange({
      start: startDate ? startDate.toISOString() : null,
      end: endDate ? endDate.toISOString() : null,
    });
    setShowDatePicker(false);
    // Clear predefined timeframe when using custom range
    if (startDate && endDate) {
      setSelectedTimeframe("");
    }
  };
  const handleDateRangeCancel = () => {
    setShowDatePicker(false);
  };

  // Calculate tooltip sales rating percentages
  const totalFilteredTooltipRatings = Object.values(filteredTooltipRatingCounts).reduce(
    (sum, count) => sum + count,
    0
  );
  const filteredTooltipSalesRatings: Rating[] = [
    {
      stars: 5,
      percentage:
        totalFilteredTooltipRatings > 0
          ? Math.round((filteredTooltipRatingCounts[5] / totalFilteredTooltipRatings) * 100)
          : 0,
      count: filteredTooltipRatingCounts[5],
    },
    {
      stars: 4,
      percentage:
        totalFilteredTooltipRatings > 0
          ? Math.round((filteredTooltipRatingCounts[4] / totalFilteredTooltipRatings) * 100)
          : 0,
      count: filteredTooltipRatingCounts[4],
    },
    {
      stars: 3,
      percentage:
        totalFilteredTooltipRatings > 0
          ? Math.round((filteredTooltipRatingCounts[3] / totalFilteredTooltipRatings) * 100)
          : 0,
      count: filteredTooltipRatingCounts[3],
    },
    {
      stars: 2,
      percentage:
        totalFilteredTooltipRatings > 0
          ? Math.round((filteredTooltipRatingCounts[2] / totalFilteredTooltipRatings) * 100)
          : 0,
      count: filteredTooltipRatingCounts[2],
    },
    {
      stars: 1,
      percentage:
        totalFilteredTooltipRatings > 0
          ? Math.round((filteredTooltipRatingCounts[1] / totalFilteredTooltipRatings) * 100)
          : 0,
      count: filteredTooltipRatingCounts[1],
    },
  ];

  // Render functions for each tab
  const renderOverallTab = () => {
    const accessData = [
      {
        name: "Help Center",
        clicks: quickAccessCounts.helpCenter,
        color: "bg-[#F15D18]",
      },
      {
        name: "Support Chat",
        clicks: quickAccessCounts.supportChat,
        color: "bg-[#F58957]",
      },
      {
        name: "Video Tutorials",
        clicks: quickAccessCounts.videoTutorials,
        color: "bg-[#9F390A]",
      },
      {
        name: "Tooltip Package",
        clicks: quickAccessCounts.tooltipPackage,
        color: "bg-[#404040]",
      },
    ];

    return (
      <>
        {" "}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5 mb-8">
          {/* Interactions Card */}
          <InteractionsCard
            title="Interactions"
            value={(
              quickAccessCounts.helpCenter +
              quickAccessCounts.supportChat +
              quickAccessCounts.videoTutorials +
              quickAccessCounts.tooltipPackage +
              sessionStats.totalInteractions +
              quickAnswersStats.totalSessions
            ).toString()}
            icon={Interaction}
            details={[
              {
                label: "Quick Access",
                value: (
                  quickAccessCounts.helpCenter +
                  quickAccessCounts.supportChat +
                  quickAccessCounts.videoTutorials +
                  quickAccessCounts.tooltipPackage
                ).toString(),
                icon: () => (
                  <img
                    src={QuickAccess}
                    alt="QuickAccess"
                    className="w-[24px] h-[24px] stroke-[4px]"
                  />
                ),
              },
              {
                label: "Quick Answers",
                value: quickAnswersStats.totalSessions.toString(),
                icon: () => (
                  <img
                    src={QuickAnswer}
                    alt="QuickAnswer"
                    className="w-[24px] h-[24px] stroke-[4px]"
                  />
                ),
              },
              {
                label: "Quick Experts",
                value: sessionStats.totalSessions.toString(),
                icon: () => (
                  <img
                    src={ExperttieSmall}
                    alt="QuickExpert"
                    className="w-[24px] h-[24px] stroke-[4px]"
                  />
                ),
              },
            ]}
          />{" "}
          {/* Amount Card */}
          <InteractionsCard
            title="Earnings"
            value={`$${(sessionStats.totalEarnings + filteredTooltipTotal).toFixed(2)}`}
            icon={AmountSpend}
            details={[
              {
                label: "Expert Sessions",
                value: `$${sessionStats.totalEarnings.toFixed(2)}`,
                icon: () => (
                  <img
                    src={videoIconSmall}
                    alt="videoIconSmall"
                    className="w-[24px] h-[24px] "
                  />
                ),
              },
              {
                label: "Tooltip Sales",
                value: `$${filteredTooltipTotal.toFixed(2)}`,
                icon: () => (
                  <img
                    src={tooltipSmall}
                    alt="tooltip"
                    className="w-[24px] h-[24px] "
                  />
                ),
              },
            ]}
          />
        </div>
        {/* Charts Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Quick Access Chart */}
          <ChartCard icon={QuickAccess} title="Quick Access" CirculTitle="">
            <BarChart items={accessData} title="No. of Clicks" />
          </ChartCard>
          {/* Question Resolution */}
          <ChartCard icon={QuickAnswer} title="Quick Answers" CirculTitle="">
            <StatsCounter
              items={[
                { label: "#Sessions", value: totalChatSessionsWithRating },
              ]}
              className="mt-4 text-[#0A0A0A]"
            />
            <CircularChart
              circulTitle="Question Resolution"
              value={quickAnswersStats.satisfactionScore || 0}
              label="% of sessions with rating 4 or 5"
              showRatings={false}
            />
          </ChartCard>{" "}
          {/* Quick Experts Chart */}
          <ChartCard icon={QuickExpert} title="Quick Experts" CirculTitle="">
            <StatsCounter
              items={[
                { label: "#Sessions", value: sessionStats.totalSessions },
              ]}
              className="mt-4"
            />
            <CircularChart
              circulTitle="Satisfaction Score"
               value={sessionStats.satisfactionScoreExpert}
              label="% of sessions with rating 4 or 5"
              showRatings={false}
            />
          </ChartCard>{" "}
          {/* Tooltip Sales Chart */}
          <ChartCard
            icon={TooltipBigicon}
            title="Tooltip Sales"
            showTotalSales={true}
            CirculTitle=""
          >
            {" "}
            <StatsCounter
              items={[{ label: "#Sales", value: filteredTooltipCount }]}
              className="mt-4"
            />
            <CircularChart
              circulTitle="Satisfaction Score"
              value={filteredTooltipSatisfaction}
              label="% of tooltip sales with rating 4 or 5"
              showRatings={false}
            />
          </ChartCard>
        </div>
        <div className="mt-7">
          {/* Expert Summary */}
          <OverallCard
            title="Expert Summary"
            icon={QuickExpertBlue}
            iconColor="bg-[#18ACF1]"
            arrowIcon={SummeryArrow}
            className=""
            details={[
              {
                label: "No. of activated accounts",
                value: activatedExpertsCount.toString(),
                key: "expertsCount",
              },
              {
                label: "Average earnings per expert ",
                value: `$${averageEarningPerExpert}`,
                key: "averageEarningPerExpert",
              },
              {
                label: "No. of active experts",
                value: activeExpertsCount.toString(),
                key: "activeExpertsCount",
              },
              {
                label: "Average activities per expert",
                value: averageActivityPerExpert,
                key: "averageActivityPerExpert",
              },
              {
                label: "No. of activities",
                value: totalActivitiesCount.toString(),
                key: "totalActivitiesCount",
              },
              {
                label: "Average rate per min",
                value: `$${averageRatePerMin.toFixed(2)} `,
                key: "averageRatePerMin",
              },
            ]}
            ratings={customerSessionRatings}
          />{" "}
          {/* Customer Summary */}
          <OverallCard
            title="Customer Summary"
            icon={CustomerSummery}
            iconColor="bg-[#F15D18]"
            arrowIcon={SummeryArrowPrimary}
            details={[
              {
                label: "No. of activated customers",
                value: activatedCustomersCount.toString(),
              },
              {
                label: "Average spent per customer ",
                value: `$${averageSpendsPerCustomer}`,
              },
              {
                label: "No. of activities",
                value: totalActivitiesCount.toString(),
              },
              {
                label: "Average activities per customer",
                value: averageActivityPerCustomer,
              },
            ]}
            ratings={expertSessionRatings}
          />
        </div>
      </>
    );
  };
  const renderCustomerTeamTab = () => {
    const accessData = [
      {
        name: "Help Center",
        clicks: quickAccessCounts.helpCenter,
        color: "bg-[#F15D18]",
      },
      {
        name: "Support Chat",
        clicks: quickAccessCounts.supportChat,
        color: "bg-[#F58957]",
      },
      {
        name: "Video Tutorials",
        clicks: quickAccessCounts.videoTutorials,
        color: "bg-[#9F390A]",
      },
      {
        name: "Tooltip Package",
        clicks: quickAccessCounts.tooltipPackage,
        color: "bg-[#404040]",
      },
    ];

    return (
      <>
        {" "}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5 mb-8">
          {/* Interactions Card */}
          <InteractionsCard
            title="Interactions"
            value={(
              quickAccessCounts.helpCenter +
              quickAccessCounts.supportChat +
              quickAccessCounts.videoTutorials +
              quickAccessCounts.tooltipPackage +
              sessionStats.totalInteractions +
              quickAnswersStats.totalSessions
            ).toString()}
            icon={Interaction}
            details={[
              {
                label: "Quick Access",
                value: (
                  quickAccessCounts.helpCenter +
                  quickAccessCounts.supportChat +
                  quickAccessCounts.videoTutorials +
                  quickAccessCounts.tooltipPackage
                ).toString(),
                icon: () => (
                  <img
                    src={QuickAccess}
                    alt="QuickAccess"
                    className="w-[24px] h-[24px] stroke-[4px]"
                  />
                ),
              },
              {
                label: "Quick Answers",
                value: quickAnswersStats.totalSessions.toString(),
                icon: () => (
                  <img
                    src={QuickAnswer}
                    alt="QuickAnswer"
                    className="w-[24px] h-[24px] stroke-[4px]"
                  />
                ),
              },
              {
                label: "Quick Experts",
                value: sessionStats.totalSessions.toString(),
                icon: () => (
                  <img
                    src={ExperttieSmall}
                    alt="QuickExpert"
                    className="w-[24px] h-[24px] stroke-[4px]"
                  />
                ),
              },
            ]}
          />

          {/* Amount Card */}
          <InteractionsCard
            title="Amount Spent"
           value={`$${(sessionStats.totalEarnings + filteredTooltipTotal).toFixed(2)}`}
            icon={AmountSpend}
            details={[
              {
                label: "Expert Sessions",
                value: `$${sessionStats.totalEarnings.toFixed(2)}`,
                icon: () => (
                  <img
                    src={videoIconSmall}
                    alt="videoIconSmall"
                    className="w-[24px] h-[24px] "
                  />
                ),
              },
              {
                label: "Tooltip Purchase",
                 value: `$${filteredTooltipTotal.toFixed(2)}`,
                icon: () => (
                  <img
                    src={tooltipSmall}
                    alt="tooltip"
                    className="w-[24px] h-[24px] "
                  />
                ),
              },
            ]}
          />
        </div>
        {/* Charts Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {" "}
          {/* Quick Access Chart */}{" "}
          <ChartCard icon={QuickAccess} title="Quick Access" CirculTitle="">
            <BarChart items={accessData} title="No. of Clicks" />
          </ChartCard>
          {/* Question Resolution */}
          <ChartCard icon={QuickAnswer} title="Quick Answers" CirculTitle=" ">
            {" "}
            <StatsCounter
              items={[
                { label: "#Sessions", value: totalChatSessionsWithRating},
              ]}
              className="mt-4 text-[#0A0A0A]"
            />
            <CircularChart
              value={quickAnswersStats.satisfactionScore || 0}
              label="% of sessions with rating 4 or 5"
              showRatings={false}
              circulTitle="Question Resolution"
            />
          </ChartCard>
          {/* Satisfaction Score renderCustomerTeamTab  */}
          <ChartCard icon={QuickExpert} title="Quick Experts" CirculTitle="">
            <StatsCounter
              items={[
                { label: "#Sessions", value: sessionStats.totalSessions },
              ]}
              className="mt-4 text-[#0A0A0A]"
            />
            <CircularChart
              value={sessionStats.satisfactionScoreExpert}
              label="% of sessions with rating 4 or 5"
              showRatings={false}
              circulTitle="Satisfaction Score"
            />
          </ChartCard>
        </div>
      </>
    );
  };
  const renderExpertTeamTab = () => {
    return (
      <>
        {/* InteractionsCard */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5 mb-8">
          {/* Interactions Card */}
          <InteractionsCard
            title="Interactions"
            value={sessionStats.totalInteractions.toString()}
            icon={Interaction}
            details={[]}
          />

          {/* Amount Card */}
          <InteractionsCard
            title="Earnings"
 value={`$${(sessionStats.totalEarnings + filteredTooltipTotal).toFixed(2)}`}
            icon={AmountSpend}
            details={[
              {
                label: "Expert Sessions",
                value: `$${sessionStats.totalEarnings.toFixed(2)}`,
                icon: () => (
                  <img
                    src={videoIconSmall}
                    alt="videoIconSmall"
                    className="w-[24px] h-[24px] "
                  />
                ),
              },
              {
                label: "Tooltip Sales",
                value: `$${filteredTooltipTotal.toFixed(2)}`,
                icon: () => (
                  <img
                    src={tooltipSmall}
                    alt="tooltip"
                    className="w-[24px] h-[24px] "
                  />
                ),
              },
            ]}
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-3 md:grid-cols-3 gap-6">
          {/* Quick Experts Card renderExpertTeamTab*/}
          <ChartCard icon={QuickExpert} title="Quick Experts" CirculTitle="">
            <StatsCounter
              items={[
                {
                  label: "#Sessions",
                  value: sessionStats.totalSessions.toString(),
                },
              ]}
              className="mt-4 text-[#0A0A0A]"
            />
            <CircularChart
  value={sessionStats.satisfactionScoreExpert}
              label="% of sessions with rating 4 or 5"
              circulTitle="Satisfaction Score"
              showRatings={true}
              ratings={expertSessionRatings}
            />
          </ChartCard>

          {/* Tooltip Sales Card */}
          <div className="relative group">
          <ChartCard
            icon={TooltipBigicon}
            title="Tooltip Sales Summary"
            CirculTitle=""
          >
            {" "}
            <StatsCounter
              items={[{ label: "#Sales", value: filteredTooltipCount }]}
              className="mt-4 text-[#0A0A0A]"
            />
            <CircularChart
              value={filteredTooltipSatisfaction}
              circulTitle="Satisfaction Score"
              label="% of tooltip sales with rating 4 or 5"
              showRatings={true}
              ratings={filteredTooltipSalesRatings}
            />
             
          </ChartCard>
          </div>
        </div>
      </>
    );
  };

  // Function to render active tab content
  const renderActiveTabContent = () => {
    switch (activeTab) {
      case "Overall":
        return renderOverallTab();
      case "Customer Team":
        return renderCustomerTeamTab();
      case "Expert Team":
        return renderExpertTeamTab();
      default:
        return renderOverallTab();
    }
  };

  return (
    <Layout>
      <div className="p-6 bg-white min-h-screen rounded-xl">
        {" "}
        {/* header */}
        <div className="mb-6 flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-[20px] nunito-semibold text-[#696969]">
              Dashboard
            </h1>
            <p className="text-[#919191] text-[16px] nunito-regular mt-1">
              Hi {profileData?.name}, easily track all your key activities and
              stay updated on your team's progress - all in one place!
            </p>
          </div>
          <div className="flex items-center">
            <label className="text-[#696969] mr-3 text-[20px] nunito-semibold whitespace-nowrap">
              Stats for:
            </label>
            <div className="relative group">
              <button
                onClick={() => setStatsDropdownOpen(!statsDropdownOpen)}
                disabled
                className="h-[40px] w-[280px] border-[#A8A8A8] border-[1px] text-[16px] text-[#696969] nunito-semibold rounded-[8px] px-3 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
              >
                {selectedStatsOption || "Me"}
                {statsDropdownOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
               {/* Tooltip on hover */}
                  <div className="absolute left-1/2 -translate-x-1/2 -top-8 px-2 py-1 bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] text-xs rounded-[8px] shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-30">
                      This feature is on its way - stay tuned!
                  </div>
              {statsDropdownOpen && (
                <div className="absolute right-0 mt-1 w-[280px] bg-white border border-[#A8A8A8] rounded-[8px] z-20 shadow-lg">
                  {statsOptions.map((option) => (
                    <div
                      key={option.value}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-[#696969] text-[14px] nunito-semibold"
                      onClick={() =>
                        handleStatsSelect(option.value, option.label)
                      }
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="mb-6 border-b-4 pb-4 mt-4">
          <div className="flex">
            <button
              className={`py-2 px-8 text-[16px] nunito-semibold ${
                activeTab === "Overall"
                  ? "border-b-4 border-[#F15D18] text-[#F15D18]"
                  : "text-[#A8A8A8]"
              }`}
              onClick={() => setActiveTab("Overall")}
            >
              Overall
            </button>
            <button
              className={`py-2 px-8 text-[16px] nunito-semibold ${
                activeTab === "Customer Team"
                  ? "border-b-4 border-[#F15D18] text-[#F15D18]"
                  : "text-[#A8A8A8]"
              }`}
              onClick={() => setActiveTab("Customer Team")}
            >
              Customer Team
            </button>
            <button
              className={`py-2 px-8 text-[16px] nunito-semibold ${
                activeTab === "Expert Team"
                  ? "border-b-4 border-[#F15D18] text-[#F15D18]"
                  : "text-[#A8A8A8]"
              }`}
              onClick={() => setActiveTab("Expert Team")}
            >
              Expert Team
            </button>
          </div>
          {/* Timeframe Selection */}
          <div className="flex items-center justify-end gap-4">
            {/* Timeframe dropdown */}
            <div className="relative" ref={timeframeRef}>
              <button
                onClick={() => setTimeframeDropdownOpen(!timeframeDropdownOpen)}
                className="text-[#404040] Roboto-regular w-[150px] text-[16px]  py-1 flex items-center gap-2  rounded"
              >
                {selectedTimeframe
                  ? timeframeOptions.find((opt) => opt.value === selectedTimeframe)
                      ?.label
                  : "Select timeframe"}
                {timeframeDropdownOpen ? (
                  <ChevronUp className="w-4 h-4 text-[#D9D9D9]" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-[#D9D9D9]" />
                )}
              </button>

              {timeframeDropdownOpen && (
                <div className="absolute left-0 top-full text-[#404040] sixe-[16px] nunito-regular z-10 mt-1 bg-white shadow-md rounded w-full">
                  {timeframeOptions.map((option) => (
                    <div
                      key={option.value}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleTimeframeSelect(option.value)}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Custom range date picker */}
            <div className="relative" ref={datePickerRef}>
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="flex items-center text-[#404040] Roboto-regular text-[16px] gap-2 px-3 py-1"
              >
                {dateRange.start && dateRange.end
                  ? `${new Date(dateRange.start).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })} - ${new Date(dateRange.end).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}`
                  : "Custom range "}
                {showDatePicker ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>

              {showDatePicker && (
                <div className="absolute right-0 top-full z-10 mt-1">
                  <DateRangePicker
                    onApply={handleDateRangeApply}
                    onCancel={handleDateRangeCancel}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Render the active tab content */}
        {renderActiveTabContent()}
      </div>
    </Layout>
  );
};

export default Dashboard;
